import type { Fact, StrategyDefinition, StrategyId } from "../model.ts";

export interface FactHelpModel {
  readonly factId: string;
  readonly strategyId: StrategyId | null;
  readonly title: string;
  readonly expression: string;
  readonly steps: readonly string[];
}

function expression(fact: Fact): string {
  return `${fact.left} ${fact.operation === "ADD" ? "+" : "−"} ${fact.right} = ${fact.answer}`;
}

function strategyTitle(
  strategyId: StrategyId | null,
  strategies: readonly StrategyDefinition[],
  fallback: string,
): string {
  return strategies.find((strategy) => strategy.id === strategyId)?.label ?? fallback;
}

export function buildFactHelp(
  fact: Fact,
  strategies: readonly StrategyDefinition[],
): FactHelpModel {
  if (fact.problemClassIds.includes("ZERO_RELATION")) {
    const strategyId = "ZERO_IDENTITY";
    return {
      factId: fact.id,
      strategyId,
      title: strategyTitle(strategyId, strategies, "Nul verandert niets"),
      expression: expression(fact),
      steps: fact.operation === "ADD"
        ? ["Als je nul erbij doet, blijft het getal hetzelfde."]
        : fact.right === 0
          ? ["Als je nul eraf haalt, blijft het getal hetzelfde."]
          : ["Als je alles eraf haalt, blijft nul over."],
    };
  }

  if (fact.problemClassIds.includes("CROSS_10_ADD")) {
    const strategyId = "VIA_10_ADD";
    const toTen = 10 - fact.left;
    const remainder = fact.right - toTen;
    return {
      factId: fact.id,
      strategyId,
      title: strategyTitle(strategyId, strategies, "Maak eerst 10"),
      expression: expression(fact),
      steps: [
        `${fact.left} heeft ${toTen} nodig om 10 te worden.`,
        `Van de ${fact.right} blijven er dan ${remainder} over.`,
        `10 + ${remainder} = ${fact.answer}.`,
      ],
    };
  }

  if (fact.problemClassIds.includes("CROSS_10_SUB")) {
    const strategyId = "BACK_VIA_10";
    const toTen = fact.left - 10;
    const remainder = fact.right - toTen;
    return {
      factId: fact.id,
      strategyId,
      title: strategyTitle(strategyId, strategies, "Ga eerst terug naar 10"),
      expression: expression(fact),
      steps: [
        `Haal eerst ${toTen} van ${fact.left} af. Dan ben je op 10.`,
        `Van de ${fact.right} moeten er dan nog ${remainder} af.`,
        `10 − ${remainder} = ${fact.answer}.`,
      ],
    };
  }

  if (fact.strategyTags.includes("DOUBLES") && fact.operation === "ADD") {
    const strategyId = "DOUBLES";
    return {
      factId: fact.id,
      strategyId,
      title: strategyTitle(strategyId, strategies, "Dubbelen"),
      expression: expression(fact),
      steps: [`Twee keer ${fact.left} is ${fact.answer}.`],
    };
  }

  if (fact.strategyTags.includes("NEAR_DOUBLE") && fact.operation === "ADD") {
    const strategyId = "NEAR_DOUBLE";
    const lower = Math.min(fact.left, fact.right);
    return {
      factId: fact.id,
      strategyId,
      title: strategyTitle(strategyId, strategies, "Bijna dubbelen"),
      expression: expression(fact),
      steps: [`${lower} + ${lower} = ${lower * 2}.`, `Eén erbij maakt ${fact.answer}.`],
    };
  }

  if (fact.problemClassIds.includes("TEEN_NO_BRIDGE")) {
    const strategyId = "TEEN_ANALOGY";
    if (fact.operation === "ADD") {
      const ones = fact.left >= 10 ? fact.left - 10 : fact.right - 10;
      const small = fact.left >= 10 ? fact.right : fact.left;
      return {
        factId: fact.id,
        strategyId,
        title: strategyTitle(strategyId, strategies, "Reken met de losse eenheden"),
        expression: expression(fact),
        steps: [`${ones} + ${small} = ${ones + small}.`, `Met de 10 erbij is dat ${fact.answer}.`],
      };
    }
    return {
      factId: fact.id,
      strategyId,
      title: strategyTitle(strategyId, strategies, "Reken met de losse eenheden"),
      expression: expression(fact),
      steps: [`${fact.left - 10} − ${fact.right} = ${fact.answer - 10}.`, `De 10 blijft staan: ${fact.answer}.`],
    };
  }

  if (fact.operation === "ADD") {
    const start = Math.max(fact.left, fact.right);
    const count = Math.min(fact.left, fact.right);
    const steps = Array.from({ length: count }, (_, index) => start + index + 1).join(", ");
    return {
      factId: fact.id,
      strategyId: null,
      title: "Tel rustig verder",
      expression: expression(fact),
      steps: [`Begin bij ${start}.`, `Tel ${count} verder: ${steps}.`],
    };
  }

  const inverse = fact.answer + fact.right;
  return {
    factId: fact.id,
    strategyId: "INVERSE_FACT",
    title: strategyTitle("INVERSE_FACT", strategies, "Plus en min horen bij elkaar"),
    expression: expression(fact),
    steps: [`${fact.answer} + ${fact.right} = ${inverse}.`, `Dus ${fact.left} − ${fact.right} = ${fact.answer}.`],
  };
}
